import type {
  RouteConfig,
  LogEntry,
  BridgeStatus,
  BusMessage,
  BusStats,
  BusHealth,
  BusAgent,
  BusScheduledTask,
  BusScheduledTaskStats,
  BusTaskRun,
  BusTaskRunStats,
  RouteRequest,
  UnmappedMessage,
  Agent,
  Persona,
  Workspace,
  SpawnTestResult,
  SpawnMeta,
  SpawnDetail,
  DelegationMeta,
  MemoryAgent,
  MemoryItem,
  ProactiveConfig,
  ProactiveTest,
  ContactSummary,
  ContactDetail,
  Mount,
  MountMode,
  DelegationDetail,
  JournalMeta,
  JournalEntry,
  Skill,
  SkillAssignments,
  SkillCatalogItem,
  SkillDetail,
  MissionControl,
  Revision,
  Route,
  BuilderMeta,
  BuilderDetail,
  PersonaCreateInput,
  JobMeta,
  JobDetail,
  GithubConnection,
  GithubRepo,
  GithubBinding,
  AsanaMentionConnection,
  AsanaMentionProject,
  AsanaMentionBinding,
  SlackMentionConnection,
  SlackMentionChannel,
} from "./types";

const API_BASE = "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const isJsonBody = typeof init?.body === "string";
  const headers: Record<string, string> = {
    ...(isJsonBody ? { "Content-Type": "application/json" } : {}),
  };
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",   // send the session cookie on every call
    ...init,
    headers: { ...headers, ...(init?.headers as object) },
  });
  if (res.status === 401) {
    // Session expired or never had one — punt to the login screen by
    // forcing a hard reload (AuthGate will re-fetch /api/auth/state).
    if (!path.startsWith("/auth/")) {
      window.location.reload();
    }
    throw new Error(`API ${path} failed (401): unauthenticated`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "Unknown error");
    throw new Error(`API ${path} failed (${res.status}): ${text}`);
  }
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const AuthApi = {
  state: () => req<{ onboarded: boolean; authenticated: boolean; public_url: string }>("/auth/state"),
  onboard: (body: { password: string; public_url?: string }) =>
    req<{ ok: boolean; public_url: string }>("/auth/onboard", { method: "POST", body: JSON.stringify(body) }),
  login: (password: string) =>
    fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    }).then(async (res) => {
      const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string; retry_after_ms?: number };
      return { status: res.status, ...body };
    }),
  logout: () => req<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  me: () => req<{ authenticated: boolean; created_at: string; expires_at: string; ip: string }>("/auth/me"),
  changePassword: (current_password: string, new_password: string) =>
    req<{ ok: boolean }>("/auth/password", { method: "POST", body: JSON.stringify({ current_password, new_password }) }),
  setPublicUrl: (public_url: string) =>
    req<{ ok: boolean; public_url: string }>("/auth/public_url", { method: "POST", body: JSON.stringify({ public_url }) }),
  openaiKey: () => req<{ set: boolean; hint: string | null }>("/auth/openai-key"),
  setOpenaiKey: (key: string) =>
    req<{ ok: boolean; set: boolean }>("/auth/openai-key", { method: "POST", body: JSON.stringify({ key }) }),
  reflectModel: () => req<{ model: string; default: string }>("/auth/reflect-model"),
  setReflectModel: (model: string) =>
    req<{ ok: boolean; model: string }>("/auth/reflect-model", { method: "POST", body: JSON.stringify({ model }) }),
  memoryConfig: () => req<{ config: MemoryConfig; defaults: MemoryConfig }>("/auth/memory-config"),
  setMemoryConfig: (patch: Partial<MemoryConfig>) =>
    req<{ ok: boolean; config: MemoryConfig }>("/auth/memory-config", { method: "POST", body: JSON.stringify(patch) }),
  inboundMax: () => req<{ mb: number; default: number }>("/auth/inbound-max"),
  setInboundMax: (mb: number | null) =>
    req<{ ok: boolean; mb: number }>("/auth/inbound-max", { method: "POST", body: JSON.stringify({ mb }) }),
  observerSweep: () => req<{ minutes: number; default: number }>("/auth/observer-sweep"),
  setObserverSweep: (minutes: number | null) =>
    req<{ ok: boolean; minutes: number }>("/auth/observer-sweep", { method: "POST", body: JSON.stringify({ minutes }) }),
  assistSessions: () => req<{ enabled: boolean }>("/auth/assist-sessions"),
  setAssistSessions: (enabled: boolean) =>
    req<{ ok: boolean; enabled: boolean }>("/auth/assist-sessions", { method: "POST", body: JSON.stringify({ enabled }) }),
  assistMaxOpen: () => req<{ max: number; default: number }>("/auth/assist-max-open"),
  setAssistMaxOpen: (max: number | null) =>
    req<{ ok: boolean; max: number }>("/auth/assist-max-open", { method: "POST", body: JSON.stringify({ max }) }),
  rpcDeadline: () => req<{ minutes: number; default: number }>("/auth/rpc-deadline"),
  setRpcDeadline: (minutes: number | null) =>
    req<{ ok: boolean; minutes: number }>("/auth/rpc-deadline", { method: "POST", body: JSON.stringify({ minutes }) }),
};

export interface MemoryConfig {
  embedModel: string; shortTtlDays: number; eventTtlDays: number;
  coldFloor: number; eventColdFloor: number; reflectMinEvents: number;
  reflectMaxEvents: number; maintenanceHours: number; injectLimit: number;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === false) continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ─── Bridge ──────────────────────────────────────────────────────────────

export const BridgeApi = {
  config: () => req<RouteConfig>("/bridge/config"),
  status: () => req<BridgeStatus>("/bridge/status"),
  // GitHub channel
  githubConnection: () => req<GithubConnection>("/bridge/github/connection"),
  saveGithubConnection: (b: { owner: string; app_id: string; private_key_path: string; webhook_secret?: string; installation_id?: number }) =>
    req<{ ok: boolean; channel_id: number }>("/bridge/github/connection", { method: "POST", body: JSON.stringify(b) }),
  setGithubConnectionEnabled: (enabled: boolean) =>
    req<{ ok: boolean; enabled: boolean }>("/bridge/github/connection/enabled", { method: "POST", body: JSON.stringify({ enabled }) }),
  githubRepos: () => req<{ repos: GithubRepo[] }>("/bridge/github/repos"),
  githubAvailableRepos: () => req<{ repos: { full_name: string; owner: string; name: string; private: boolean }[] }>("/bridge/github/available-repos"),
  saveGithubRepo: (b: { owner: string; repo: string; enabled?: boolean; instructions?: string | null; trusted_users?: string[] | null; base_branch?: string; pr_only?: boolean; max_concurrent?: number; personas?: GithubBinding[] }) =>
    req<{ ok: boolean; id: number }>("/bridge/github/repos", { method: "POST", body: JSON.stringify(b) }),
  deleteGithubRepo: (id: number) => req<{ ok: boolean }>(`/bridge/github/repos/${id}`, { method: "DELETE" }),
  // Asana @-mentions (separate from the asana CHANNEL — see PLAN-asana-mentions.md)
  asanaMentionConnection: () => req<AsanaMentionConnection>("/bridge/asana/connection"),
  saveAsanaMentionConnection: (b: { personal_access_token?: string; enabled?: boolean; max_concurrent?: number; poll_interval_s?: number }) =>
    req<{ ok: boolean }>("/bridge/asana/connection", { method: "POST", body: JSON.stringify(b) }),
  disconnectAsanaMentions: () => req<{ ok: boolean }>("/bridge/asana/connection", { method: "DELETE" }),
  asanaMentionProjects: () => req<{ projects: AsanaMentionProject[] }>("/bridge/asana/projects"),
  saveAsanaMentionProject: (b: { project_gid: string; name?: string | null; enabled?: boolean; instructions?: string | null; trusted_users?: string[] | null; personas?: AsanaMentionBinding[] }) =>
    req<{ ok: boolean; id: number }>("/bridge/asana/projects", { method: "POST", body: JSON.stringify(b) }),
  deleteAsanaMentionProject: (id: number) => req<{ ok: boolean }>(`/bridge/asana/projects/${id}`, { method: "DELETE" }),
  // Slack @-mentions — the Alfred bot (separate from the slack CHANNEL — see PLAN-slack-mentions.md)
  slackMentionConnection: () => req<SlackMentionConnection>("/bridge/slack-mentions/connection"),
  saveSlackMentionConnection: (b: { bot_token?: string; signing_secret?: string; enabled?: boolean }) =>
    req<{ ok: boolean }>("/bridge/slack-mentions/connection", { method: "POST", body: JSON.stringify(b) }),
  disconnectSlackMentions: () => req<{ ok: boolean }>("/bridge/slack-mentions/connection", { method: "DELETE" }),
  slackMentionChannels: () => req<{ channels: SlackMentionChannel[] }>("/bridge/slack-mentions/channels"),
  slackBotChannels: () => req<{ ok: boolean; channels?: Array<{ id: string; name: string }>; error?: string }>("/bridge/slack-mentions/available-channels"),
  saveSlackMentionChannel: (b: { channel_id: string; name?: string | null; enabled?: boolean; trusted_users?: string[] | null }) =>
    req<{ ok: boolean }>("/bridge/slack-mentions/channels", { method: "POST", body: JSON.stringify(b) }),
  deleteSlackMentionChannel: (id: string) => req<{ ok: boolean }>(`/bridge/slack-mentions/channels/${id}`, { method: "DELETE" }),
  restart: () => req<void>("/bridge/restart", { method: "POST" }),
  /** Restart the bridge and poll /status until it's back up (or timeout).
   *  Used by the channel wizards so newly-added channels begin ingesting
   *  without the operator manually running systemctl. */
  restartAndWait: async (timeoutMs = 30_000): Promise<void> => {
    await req<void>("/bridge/restart", { method: "POST" });
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 800));
      try {
        const s = await req<BridgeStatus>("/bridge/status");
        if (s.running) return;
      } catch { /* still down */ }
    }
    throw new Error("bridge did not come back up within timeout");
  },
  simulate: (evt: { platform: string; chat_jid: string; chat_kind: string; sender_jid?: string; text?: string }) =>
    req<{ matched: boolean; route?: { name: string; persona: string; acl: { inbound: string } } }>(
      "/bridge/simulate",
      { method: "POST", body: JSON.stringify(evt) },
    ),
  logs: (opts?: { level?: string; search?: string; limit?: number }) =>
    req<LogEntry[]>(`/bridge/logs${qs(opts ?? {})}`),
  agents: () => req<Record<string, Agent>>("/bridge/agents"),
  saveAgent: (name: string, agent: Agent) =>
    req<void>(`/bridge/agents/${encodeURIComponent(name)}`, { method: "POST", body: JSON.stringify(agent) }),
  /** True rename — single UPDATE; FK cascades to personas.agent_name. */
  renameAgent: (oldName: string, newName: string) =>
    req<{ ok: boolean; old_name: string; new_name: string }>(
      `/bridge/agents/${encodeURIComponent(oldName)}`,
      { method: "PATCH", body: JSON.stringify({ new_name: newName }) },
    ),
  deleteAgent: (name: string) => req<void>(`/bridge/agents/${encodeURIComponent(name)}`, { method: "DELETE" }),
  personas: () => req<Record<string, Persona>>("/bridge/personas"),
  savePersona: (name: string, p: Persona) =>
    req<void>(`/bridge/personas/${encodeURIComponent(name)}`, { method: "POST", body: JSON.stringify(p) }),
  renamePersona: (oldName: string, newName: string) =>
    req<{ ok: boolean; old_name: string; new_name: string }>(
      `/bridge/personas/${encodeURIComponent(oldName)}`,
      { method: "PATCH", body: JSON.stringify({ new_name: newName }) },
    ),
  deletePersona: (name: string) => req<void>(`/bridge/personas/${encodeURIComponent(name)}`, { method: "DELETE" }),
  personaPrompt: (name: string) => req<{ content: string }>(`/bridge/personas/${encodeURIComponent(name)}/prompt`),
  savePersonaPrompt: (name: string, content: string) =>
    req<void>(`/bridge/personas/${encodeURIComponent(name)}/prompt`, { method: "POST", body: JSON.stringify({ content }) }),
  tuiStatus: (name: string) => req<{ running: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/tui`),
  startTui: (name: string, opts?: { model?: string; effort?: string }) =>
    req<{ ok: boolean; running: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/tui`, { method: "POST", body: JSON.stringify(opts ?? {}) }),
  stopTui: (name: string) => req<{ ok: boolean; running: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/tui`, { method: "DELETE" }),
  delegateAllowlist: () => req<{ agents: string[] }>("/bridge/delegate-allowlist"),
  setDelegate: (name: string, enabled: boolean) =>
    req<{ ok: boolean; can_delegate: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/delegate`, { method: "POST", body: JSON.stringify({ enabled }) }),
  escalationAllowlist: () => req<{ agents: string[] }>("/bridge/escalation-allowlist"),
  setEscalation: (name: string, enabled: boolean) =>
    req<{ ok: boolean; can_escalate: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/escalate`, { method: "POST", body: JSON.stringify({ enabled }) }),
  mcpOverview: () => req<import("./types").McpOverview>("/bridge/mcp/overview"),
  addMcpServer: (body: Record<string, unknown>) =>
    req<{ ok?: boolean; name?: string; error?: string }>("/bridge/mcp/servers", { method: "POST", body: JSON.stringify(body) }),
  deleteMcpServer: (name: string) =>
    req<{ ok: boolean }>(`/bridge/mcp/servers/${encodeURIComponent(name)}`, { method: "DELETE" }),
  fillMcpHeaders: (name: string, headers: Record<string, string>) =>
    req<{ ok?: boolean; missing?: string[]; error?: string }>(`/bridge/mcp/servers/${encodeURIComponent(name)}/headers`, { method: "POST", body: JSON.stringify({ headers }) }),
  setMcpGrant: (persona: string, server: string, mode: import("./types").McpGrantMode, tools: string[]) =>
    req<{ ok: boolean }>(`/bridge/mcp/grants/${encodeURIComponent(persona)}/${encodeURIComponent(server)}`, { method: "POST", body: JSON.stringify({ mode, tools }) }),
  inlineTools: (persona: string) =>
    req<import("./types").InlineToolsData>(`/bridge/mcp/inline/${encodeURIComponent(persona)}`),
  setInlineTool: (persona: string, tool: string, enabled: boolean) =>
    req<{ ok: boolean; inline: string[] }>(`/bridge/mcp/inline/${encodeURIComponent(persona)}`, { method: "POST", body: JSON.stringify({ tool, enabled }) }),
  mcpOAuthStart: (server: string) =>
    req<{ ok?: boolean; authUrl?: string; authorized?: boolean; error?: string }>(`/bridge/mcp/oauth/start/${encodeURIComponent(server)}`, { method: "POST" }),
  mcpOAuthStartManual: (server: string) =>
    req<{ ok?: boolean; authUrl?: string; authorized?: boolean; error?: string }>(`/bridge/mcp/oauth/start-manual/${encodeURIComponent(server)}`, { method: "POST" }),
  mcpOAuthFinish: (state: string, code: string) =>
    req<{ ok?: boolean; server?: string; error?: string }>(`/bridge/mcp/oauth/finish`, { method: "POST", body: JSON.stringify({ state, code }) }),
  capabilities: () => req<{ capabilities: Record<string, { jobs: boolean; messaging: boolean }>; groups: string[] }>("/bridge/capabilities"),
  setCapability: (persona: string, capability: "jobs" | "messaging", enabled: boolean) =>
    req<{ ok: boolean; capabilities: { jobs: boolean; messaging: boolean } }>(`/bridge/capabilities/${encodeURIComponent(persona)}`, { method: "POST", body: JSON.stringify({ capability, enabled }) }),
  proactiveConfigs: () => req<{ configs: ProactiveConfig[] }>("/bridge/proactive"),
  setProactive: (persona: string, patch: Partial<ProactiveConfig>) =>
    req<{ ok: boolean; config: ProactiveConfig }>(`/bridge/proactive/${encodeURIComponent(persona)}`, { method: "POST", body: JSON.stringify(patch) }),
  testProactive: (persona: string, chat_id: string) =>
    req<ProactiveTest>("/bridge/proactive/test", { method: "POST", body: JSON.stringify({ persona, chat_id }) }),
  sandboxOverrides: () => req<{ global_default: boolean; overrides: Record<string, boolean> }>("/bridge/sandbox-overrides"),
  setSandbox: (name: string, enabled: boolean | null) =>
    req<{ ok: boolean; sandbox: boolean | "default" }>(`/bridge/personas/${encodeURIComponent(name)}/sandbox`, { method: "POST", body: JSON.stringify({ enabled }) }),
  personaMounts: (name: string) => req<{ mounts: Mount[]; inherited: (Mount & { source: string })[] }>(`/bridge/personas/${encodeURIComponent(name)}/mounts`),
  addMount: (name: string, path: string, mode: MountMode) =>
    req<{ ok: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/mounts`, { method: "POST", body: JSON.stringify({ path, mode }) }),
  removeMount: (name: string, path: string) =>
    req<{ ok: boolean }>(`/bridge/personas/${encodeURIComponent(name)}/mounts?path=${encodeURIComponent(path)}`, { method: "DELETE" }),
  agentMounts: (name: string) => req<{ mounts: Mount[]; inherited: (Mount & { source: string })[] }>(`/bridge/agents/${encodeURIComponent(name)}/mounts`),
  addAgentMount: (name: string, path: string, mode: MountMode) =>
    req<{ ok: boolean }>(`/bridge/agents/${encodeURIComponent(name)}/mounts`, { method: "POST", body: JSON.stringify({ path, mode }) }),
  removeAgentMount: (name: string, path: string) =>
    req<{ ok: boolean }>(`/bridge/agents/${encodeURIComponent(name)}/mounts?path=${encodeURIComponent(path)}`, { method: "DELETE" }),
  globalMounts: () => req<{ mounts: Mount[] }>("/bridge/global-mounts"),
  addGlobalMount: (path: string, mode: MountMode) =>
    req<{ ok: boolean }>("/bridge/global-mounts", { method: "POST", body: JSON.stringify({ path, mode }) }),
  removeGlobalMount: (path: string) =>
    req<{ ok: boolean }>(`/bridge/global-mounts?path=${encodeURIComponent(path)}`, { method: "DELETE" }),
  saveRoute: (name: string, route: Route) =>
    req<void>(`/bridge/routes/${encodeURIComponent(name)}`, { method: "POST", body: JSON.stringify(route) }),
  deleteRoute: (name: string, opts?: { clearAssistRefs?: boolean }) =>
    req<{ ok: boolean; clearedRoutes?: number; clearedChannels?: number }>(
      `/bridge/routes/${encodeURIComponent(name)}${opts?.clearAssistRefs ? "?clear=1" : ""}`,
      { method: "DELETE" },
    ),
  reorderRoutes: (order: string[]) =>
    req<void>("/bridge/routes/_reorder", { method: "POST", body: JSON.stringify({ order }) }),
  channels: () => req<import("./types").Channel[]>("/bridge/channels"),
  channel: (id: number) => req<import("./types").Channel>(`/bridge/channels/${id}`),
  waRelink: () => req<{ ok?: boolean; error?: string }>("/bridge/wa/relink", { method: "POST" }),
  waRelinkFresh: () => req<{ ok?: boolean; error?: string }>("/bridge/wa/relink-fresh", { method: "POST" }),
  waRelinkStatus: () => req<{ status: string; code: string | null; message: string; updated_at?: string }>("/bridge/wa/relink/status"),
  validateChannel: (body: { platform: string; credentials?: Record<string, unknown> }) =>
    req<{ ok: boolean; identifier?: string; details?: Record<string, unknown>; hints?: Record<string, unknown>; error?: string }>(
      "/bridge/channels/validate",
      { method: "POST", body: JSON.stringify(body) },
    ),
  createChannel: (body: Record<string, unknown> & { name: string; platform: string }) =>
    req<import("./types").Channel>("/bridge/channels", { method: "POST", body: JSON.stringify(body) }),
  updateChannel: (id: number, patch: Record<string, unknown>) =>
    req<import("./types").Channel>(`/bridge/channels/${id}`, { method: "POST", body: JSON.stringify(patch) }),
  deleteChannel: (id: number) => req<void>(`/bridge/channels/${id}`, { method: "DELETE" }),
  /** Provision observer routes for a channel's pending requests using its defaults.
   *  dryRun → just the count; chat → provision a single chat (per-request apply). */
  provisionObservers: (id: number, opts?: { dryRun?: boolean; chat?: string }) => {
    const p = new URLSearchParams();
    if (opts?.dryRun) p.set("dry_run", "1");
    if (opts?.chat) p.set("chat", opts.chat);
    const q = p.toString();
    return req<{ ok?: boolean; pending?: number; provisioned?: number; error?: string }>(
      `/bridge/channels/${id}/provision-observers${q ? `?${q}` : ""}`, { method: "POST" });
  },
  asanaProjects: (pat: string) =>
    req<{ ok: boolean; projects?: Array<{ gid: string; name: string; workspace: { gid: string; name: string } }>; error?: string }>(
      "/bridge/channels/asana-projects",
      { method: "POST", body: JSON.stringify({ pat }) },
    ),
  /** Same as above but for an existing channel: uses the stored PAT and
   *  annotates each project with `subscribed: boolean`. */
  channelAsanaProjects: (channelId: number) =>
    req<{
      ok: boolean;
      projects?: Array<{ gid: string; name: string; workspace: { gid: string; name: string }; subscribed: boolean }>;
      error?: string;
    }>(`/bridge/channels/${channelId}/asana-projects`),
  /** New-channel wizard: verify raw IMAP/SMTP creds + list folders (no channel
   *  yet). The email analog of asanaProjects. */
  emailVerify: (credentials: Record<string, unknown>) =>
    req<{ ok: boolean; identifier?: string; folders?: string[]; error?: string }>(
      "/bridge/channels/email-verify",
      { method: "POST", body: JSON.stringify({ credentials }) },
    ),
  /** IMAP folders/labels for an existing email channel (uses the stored creds),
   *  each annotated with `subscribed: boolean`. The label-filter analog of
   *  channelAsanaProjects. */
  channelEmailFolders: (channelId: number) =>
    req<{
      ok: boolean;
      folders?: Array<{ path: string; subscribed: boolean }>;
      error?: string;
    }>(`/bridge/channels/${channelId}/email-folders`),
  // ── Slack (user-token channel) ──
  slackVerify: (token: string) =>
    req<{ ok: boolean; identifier?: string; team?: string; user?: string; url?: string; error?: string }>(
      "/bridge/channels/slack-verify", { method: "POST", body: JSON.stringify({ token }) }),
  slackChannels: (token: string) =>
    req<{ ok: boolean; channels?: Array<{ id: string; name: string; kind: string }>; error?: string }>(
      "/bridge/channels/slack-channels", { method: "POST", body: JSON.stringify({ token }) }),
  /** Channels for an existing slack channel (stored token), annotated subscribed. */
  channelSlackChannels: (channelId: number) =>
    req<{ ok: boolean; channels?: Array<{ id: string; name: string; kind: string; subscribed: boolean }>; error?: string }>(
      `/bridge/channels/${channelId}/slack-channels`),
  subscriptions: (channelId: number) =>
    req<{
      subscriptions: Array<{ channel_id: number; kind: string; external_id: string; name: string | null; enabled: number; added_at: string }>;
    }>(`/bridge/channels/${channelId}/subscriptions`),
  setSubscriptions: (
    channelId: number,
    kind: string,
    items: Array<{ external_id: string; name?: string }>,
    opts?: { cascadeDisableRoutes?: boolean },
  ) =>
    req<{
      ok: boolean;
      count: number;
      removed: number;
      disabled_routes: Array<{ name: string; chat_jid: string; project_gid: string }>;
    }>(
      `/bridge/channels/${channelId}/subscriptions`,
      {
        method: "POST",
        body: JSON.stringify({
          kind,
          items,
          cascade_disable_routes: opts?.cascadeDisableRoutes,
        }),
      },
    ),
  workspaces: () => req<Record<string, Workspace>>("/bridge/workspaces"),
  spawnTest: (body: { agentName: string; config?: Agent; soul: string; prompt?: string }) =>
    req<SpawnTestResult>("/bridge/spawn-test", { method: "POST", body: JSON.stringify(body) }),
  journals: () => req<JournalMeta[]>("/bridge/journals"),
  journal: (id: string) => req<{ id: string; entries: JournalEntry[] }>(`/bridge/journals/${id}`),
  saveJournal: (id: string, entries: unknown[]) =>
    req<void>(`/bridge/journals/${id}`, { method: "POST", body: JSON.stringify({ entries }) }),
  truncateJournal: (id: string, keep: number) =>
    req<void>(`/bridge/journals/${id}?keep=${keep}`, { method: "DELETE" }),
  deleteJournal: (id: string) => req<void>(`/bridge/journals/${id}`, { method: "DELETE" }),
};

// ─── Bus ─────────────────────────────────────────────────────────────────

export const BusApi = {
  messages: (opts?: { to?: string; from?: string; unread_only?: boolean; include_scheduler?: boolean; limit?: number }) =>
    req<BusMessage[]>(`/bus/messages${qs(opts ?? {})}`),
  message: (id: string) => req<BusMessage>(`/bus/messages/${id}`),
  replies: (id: string) => req<BusMessage[]>(`/bus/messages/${id}/replies`),
  markRead: (id: string, read: boolean) =>
    req<void>(`/bus/messages/${id}/read`, { method: "POST", body: JSON.stringify({ read }) }),
  deleteMessage: (id: string) => req<void>(`/bus/messages/${id}`, { method: "DELETE" }),
  agents: () => req<BusAgent[]>("/bus/agents"),
  stats: () => req<BusStats>("/bus/stats"),
  health: () => req<BusHealth>("/bus/health"),
  scheduledTasks: (opts?: { target?: string; status?: string }) =>
    req<BusScheduledTask[]>(`/bus/scheduled-tasks${qs(opts ?? {})}`),
  scheduledTask: (id: string) => req<BusScheduledTask>(`/bus/scheduled-tasks/${id}`),
  scheduledTaskStats: () => req<BusScheduledTaskStats>("/bus/scheduled-tasks/stats"),
  createScheduledTask: (t: Partial<BusScheduledTask>) =>
    req<{ id: string; next_fire_at: string | null }>("/bus/scheduled-tasks", { method: "POST", body: JSON.stringify(t) }),
  updateScheduledTask: (id: string, t: Partial<BusScheduledTask>) =>
    req<void>(`/bus/scheduled-tasks/${id}`, { method: "POST", body: JSON.stringify(t) }),
  deleteScheduledTask: (id: string) => req<void>(`/bus/scheduled-tasks/${id}`, { method: "DELETE" }),
  runScheduledTask: (id: string) => req<{ ok: boolean; message_id: string }>(`/bus/scheduled-tasks/${id}/run`, { method: "POST" }),
  taskRuns: (opts?: { task_id?: string; limit?: number; offset?: number }) =>
    req<BusTaskRun[]>(`/bus/task-runs${qs(opts ?? {})}`),
  taskRunStats: (taskId?: string) =>
    req<BusTaskRunStats>(`/bus/task-runs/stats${qs({ task_id: taskId })}`),
};

// ─── Requests ────────────────────────────────────────────────────────────

export interface RequestsPage {
  items: RouteRequest[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export const RequestsApi = {
  list: (opts?: {
    showHidden?: boolean;
    status?: "unresolved" | "resolved" | "all";
    platform?: string;
    channel?: string;
    q?: string;
    page?: number;
    pageSize?: number;
  }) => req<RequestsPage>(`/requests${qs({
    showHidden: opts?.showHidden ?? false,
    status: opts?.status,
    platform: opts?.platform,
    channel: opts?.channel,
    q: opts?.q,
    page: opts?.page,
    pageSize: opts?.pageSize,
  })}`),
  conversation: (chatJid: string) => req<UnmappedMessage[]>(`/requests/${encodeURIComponent(chatJid)}/conversation`),
  hide: (id: string) => req<void>(`/requests/${id}/hide`, { method: "POST" }),
  resolve: (id: string) => req<void>(`/requests/${id}/resolve`, { method: "POST" }),
  dismiss: (id: string) => req<void>(`/requests/${id}/dismiss`, { method: "POST" }),
  hideChat: (chat: string) => req<void>(`/requests/by-chat/${encodeURIComponent(chat)}/hide`, { method: "POST" }),
  resolveChat: (chat: string) => req<void>(`/requests/by-chat/${encodeURIComponent(chat)}/resolve`, { method: "POST" }),
  dismissChat: (chat: string) => req<void>(`/requests/by-chat/${encodeURIComponent(chat)}/dismiss`, { method: "POST" }),
  /** Enqueue the chat (+ its links) into the wiki, then resolve it. */
  wikiChat: (chat: string) => req<{ ok: boolean; submitted: number; links: number }>(`/requests/by-chat/${encodeURIComponent(chat)}/wiki`, { method: "POST" }),
  createRoute: (request_id: string, route: Route & { channel?: string }) =>
    req<void>("/requests/create-route", { method: "POST", body: JSON.stringify({ request_id, route }) }),
};

// ─── Skills ──────────────────────────────────────────────────────────────

export const SkillsApi = {
  list: () => req<Skill[]>("/skills"),
  fleetAgents: () => req<string[]>("/skills/agents"),
  assignments: () => req<SkillAssignments>("/skills/assignments"),
  saveAssignments: (assignments: SkillAssignments) =>
    req<{ ok: boolean; synced: Record<string, { added: string[]; removed: string[] }> }>(
      "/skills/assignments",
      { method: "POST", body: JSON.stringify({ assignments }) },
    ),
  enable: (agent: string, skill: string) =>
    req<{ ok: boolean }>(`/skills/${encodeURIComponent(agent)}/enable`, { method: "POST", body: JSON.stringify({ skill }) }),
  disable: (agent: string, skill: string) =>
    req<{ ok: boolean }>(`/skills/${encodeURIComponent(agent)}/disable`, { method: "POST", body: JSON.stringify({ skill }) }),
};

export const CatalogApi = {
  list: () => req<SkillCatalogItem[]>("/skills/catalog"),
  detail: (name: string) => req<SkillDetail>(`/skills/catalog/${encodeURIComponent(name)}`),
  saveFile: (name: string, file_path: string, content: string) =>
    req<void>(`/skills/catalog/${encodeURIComponent(name)}`, { method: "POST", body: JSON.stringify({ file_path, content }) }),
  deleteSkill: (name: string) => req<void>(`/skills/catalog/${encodeURIComponent(name)}`, { method: "DELETE" }),
  uploadFile: (name: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return req<{ ok: boolean; name: string; isZip: boolean }>(
      `/skills/catalog/${encodeURIComponent(name)}/upload`,
      { method: "POST", body: form as unknown as BodyInit },
    );
  },
  // Clean-REPLACE the skill from a .zip/.skill package: orphaned files are
  // dropped, wrappers unwrapped, a committed .env preserved if the package lacks one.
  replaceSkill: (name: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return req<{ ok: boolean; name: string; files: number; kept_env: boolean }>(
      `/skills/catalog/${encodeURIComponent(name)}/replace`,
      { method: "POST", body: form as unknown as BodyInit },
    );
  },
  // The whole skill as a .skill package, exactly as it is on disk. A plain link:
  // the session cookie rides along.
  downloadUrl: (name: string) => `${API_BASE}/skills/catalog/${encodeURIComponent(name)}/download`,
  // Import a whole NEW skill from a .skill/.zip package or a SKILL.md.
  importSkill: (file: File, overwrite = false) => {
    const form = new FormData();
    form.append("file", file);
    return req<{ ok: boolean; name: string; files: number }>(
      `/skills/catalog/import${overwrite ? "?overwrite=true" : ""}`,
      { method: "POST", body: form as unknown as BodyInit },
    );
  },
};

// Interactive, agent-assisted skill authoring. Proxied to the bridge; SSE via
// EventSource at /api/skill-builder/:id/stream.
export const SkillBuilderApi = {
  start: (goal: string, backend = "claude-sub") =>
    req<BuilderMeta>("/skill-builder", { method: "POST", body: JSON.stringify({ goal, backend, requested_by: "operator" }) }),
  list: () => req<{ builders: BuilderMeta[] }>("/skill-builder").then((r) => r.builders),
  get: (id: string) => req<BuilderDetail>(`/skill-builder/${encodeURIComponent(id)}`),
  turn: (id: string, message: string) =>
    req<{ ok: boolean; error?: string }>(`/skill-builder/${encodeURIComponent(id)}/turn`, { method: "POST", body: JSON.stringify({ message }) }),
  cancel: (id: string) => req<{ ok: boolean }>(`/skill-builder/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  file: (id: string, path: string) => req<{ path: string; content: string }>(`/skill-builder/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`),
  import: (id: string, opts: { name?: string; overwrite?: boolean; allowSecrets?: boolean } = {}) =>
    req<{ ok: boolean; name: string; files: number }>(`/skill-builder/${encodeURIComponent(id)}/import`, { method: "POST", body: JSON.stringify(opts) }),
};

// Interactive, agent-assisted MCP-server authoring. Mirrors SkillBuilderApi, but
// "test" connects with the drafted config + lists tools, and "import" registers
// the server in the gateway. Both accept optional cred overrides for placeholders.
export const McpBuilderApi = {
  start: (goal: string, backend = "claude-sub") =>
    req<BuilderMeta>("/mcp-builder", { method: "POST", body: JSON.stringify({ goal, backend, requested_by: "operator" }) }),
  list: () => req<{ builders: BuilderMeta[] }>("/mcp-builder").then((r) => r.builders),
  get: (id: string) => req<BuilderDetail>(`/mcp-builder/${encodeURIComponent(id)}`),
  turn: (id: string, message: string) =>
    req<{ ok: boolean; error?: string }>(`/mcp-builder/${encodeURIComponent(id)}/turn`, { method: "POST", body: JSON.stringify({ message }) }),
  cancel: (id: string) => req<{ ok: boolean }>(`/mcp-builder/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  file: (id: string, path: string) => req<{ path: string; content: string }>(`/mcp-builder/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`),
  test: (id: string, creds: { headers?: Record<string, string>; env?: Record<string, string> } = {}) =>
    req<{ ok: boolean; tools?: { name: string; description?: string }[]; error?: string }>(`/mcp-builder/${encodeURIComponent(id)}/test`, { method: "POST", body: JSON.stringify(creds) }),
  import: (id: string, opts: { name?: string; headers?: Record<string, string>; env?: Record<string, string> } = {}) =>
    req<{ ok?: boolean; name?: string; tools?: { name: string }[]; connected?: boolean; error?: string }>(`/mcp-builder/${encodeURIComponent(id)}/import`, { method: "POST", body: JSON.stringify(opts) }),
};

// Interactive, agent-assisted persona authoring. Mirrors SkillBuilderApi, but
// "create" provisions a full persona (DB row, token, workdir, skills, route).
export const PersonaBuilderApi = {
  start: (goal: string, backend = "claude-sub") =>
    req<BuilderMeta>("/persona-builder", { method: "POST", body: JSON.stringify({ goal, backend, requested_by: "operator" }) }),
  list: () => req<{ builders: BuilderMeta[] }>("/persona-builder").then((r) => r.builders),
  get: (id: string) => req<BuilderDetail>(`/persona-builder/${encodeURIComponent(id)}`),
  turn: (id: string, message: string) =>
    req<{ ok: boolean; error?: string }>(`/persona-builder/${encodeURIComponent(id)}/turn`, { method: "POST", body: JSON.stringify({ message }) }),
  cancel: (id: string) => req<{ ok: boolean }>(`/persona-builder/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  file: (id: string, path: string) => req<{ path: string; content: string }>(`/persona-builder/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`),
  create: (id: string, opts: PersonaCreateInput) =>
    req<{ ok: boolean; name: string; agent: string; skills: number; route?: string }>(`/persona-builder/${encodeURIComponent(id)}/create`, { method: "POST", body: JSON.stringify(opts) }),
};

// ─── Spawns ──────────────────────────────────────────────────────────────

export const SpawnsApi = {
  list: (opts?: { persona?: string; status?: string; limit?: number }) =>
    req<SpawnMeta[]>(`/spawns${qs(opts ?? {})}`),
  detail: (id: string) => req<SpawnDetail>(`/spawns/${id}`),
  kill: (id: string) =>
    req<{ ok: boolean; pid?: number; already_dead?: boolean }>(`/spawns/${id}/kill`, { method: "POST" }),
  // Re-runs the ORIGINATING work (fresh github token + checkout), not the recorded
  // command — replaying that would point the persona at a deleted checkout.
  retry: (id: string) =>
    req<{ ok: boolean; spawn: string; queued?: boolean }>(`/spawns/${id}/retry`, { method: "POST" }),
};

export const MemoryApi = {
  agents: () => req<MemoryAgent[]>("/memory/agents"),
  list: (agent: string, opts?: { q?: string; tier?: string; review?: string }) =>
    req<MemoryItem[]>(`/memory/${encodeURIComponent(agent)}${qs(opts ?? {})}`),
  forget: (agent: string, id: number) =>
    req<{ ok: boolean; deleted: number }>(`/memory/${encodeURIComponent(agent)}/${id}`, { method: "DELETE" }),
  confirm: (agent: string, id: number) =>
    req<{ ok: boolean }>(`/memory/${encodeURIComponent(agent)}/${id}/confirm`, { method: "POST" }),
};

export const ContactsApi = {
  list: () => req<ContactSummary[]>("/contacts"),
  get: (id: number) => req<ContactDetail>(`/contacts/${id}`),
  merge: (from: number, into: number) =>
    req<{ ok: boolean }>(`/contacts/${from}/merge`, { method: "POST", body: JSON.stringify({ into }) }),
  setPrimaryName: (id: number, display_name: string) =>
    req<{ ok: boolean }>(`/contacts/${id}`, { method: "PATCH", body: JSON.stringify({ display_name }) }),
};

export const JobsApi = {
  list: (opts?: { status?: string; requested_by?: string; limit?: number }) =>
    req<JobMeta[]>(`/jobs${qs(opts ?? {})}`),
  detail: (id: string) => req<JobDetail>(`/jobs/${id}`),
  kill: (id: string) =>
    req<{ ok: boolean; pid?: number; already_dead?: boolean }>(`/jobs/${id}/kill`, { method: "POST" }),
};

export const DelegationsApi = {
  list: (opts?: { agent?: string; status?: string; limit?: number }) =>
    req<DelegationMeta[]>(`/delegations${qs(opts ?? {})}`),
  detail: (id: string) => req<DelegationDetail>(`/delegations/${id}`),
  kill: (id: string) =>
    req<{ ok: boolean; pid?: number; already_dead?: boolean }>(`/delegations/${id}/kill`, { method: "POST" }),
};

// ─── Agent tasks (durable per-agent work ledger) ──────────────────────────
export type TaskStatus = "backlog" | "active" | "held" | "blocked" | "done" | "cancelled";
export interface TaskRow {
  id: string; owner_agent: string; title: string; description: string | null;
  status: TaskStatus; focus: number; priority: number; origin: string | null;
  created_by: string | null; conversation_id: string | null; contact_id: string | null;
  blocked_reason: string | null; resume_schedule_id: string | null; resolution: string | null;
  note_count: number; created_at: string; updated_at: string; focused_at: string | null; done_at: string | null;
}
export interface TaskCheckpoint { ts: string; kind: "created" | "note" | "status"; by: string; body: string }
export const TasksApi = {
  list: (opts?: { agent?: string; status?: TaskStatus }) =>
    req<{ tasks: TaskRow[] }>(`/tasks${qs(opts ?? {})}`),
  detail: (id: string) => req<{ task: TaskRow; journal: TaskCheckpoint[] }>(`/tasks/${id}`),
  create: (body: { agent: string; title: string; description?: string; priority?: number }) =>
    req<{ id: string; status: string }>(`/tasks`, { method: "POST", body: JSON.stringify(body) }),
  cancel: (id: string, reason?: string) =>
    req<{ id: string; status: string }>(`/tasks/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  setPriority: (id: string, priority: number) =>
    req<{ id: string; priority: number }>(`/tasks/${id}/priority`, { method: "POST", body: JSON.stringify({ priority }) }),
  resume: (id: string) =>
    req<{ id: string; owner: string; message_id: string }>(`/tasks/${id}/resume`, { method: "POST" }),
};

// ─── Revisions ───────────────────────────────────────────────────────────

export const RevisionsApi = {
  list: (opts?: { object_type?: string; object_name?: string; limit?: number }) =>
    req<Revision[]>(`/revisions${qs(opts ?? {})}`),
  get: (id: number) => req<Revision>(`/revisions/${id}`),
  revert: (id: number) => req<{ ok: boolean }>(`/revisions/${id}/revert`, { method: "POST" }),
};

// ─── Persona sprites (Visualize characters) ─────────────────────────────

export interface SpriteJob {
  persona: string;
  status: "describing" | "generating" | "done" | "error";
  keyword: string | null;
  description: string | null;
  error: string | null;
  updated_at: string;
  has_png?: boolean;
}

export interface SpriteConfig {
  model: string;
  quality: string;
  size: number;
  style_prompt: string;
  defaults: { model: string; quality: string; size: number; style_prompt: string };
  key_set: boolean;
}

export const SpritesApi = {
  list: () => req<SpriteJob[]>("/sprites"),
  generate: (persona: string, keyword?: string) =>
    req<SpriteJob>(`/sprites/${persona}/generate`, { method: "POST", body: JSON.stringify({ keyword }) }),
  config: () => req<SpriteConfig>("/sprites/config"),
  setConfig: (patch: Partial<Pick<SpriteConfig, "model" | "quality" | "size" | "style_prompt">>) =>
    req<SpriteConfig>("/sprites/config", { method: "POST", body: JSON.stringify(patch) }),
  pngUrl: (persona: string, cacheKey?: string) =>
    `/api/sprites/${persona}/png${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ""}`,
  generateContact: (contactId: number, keyword?: string) =>
    req<SpriteJob>(`/sprites/contact/${contactId}/generate`, { method: "POST", body: JSON.stringify({ keyword }) }),
  contactPngUrl: (contactId: number, cacheKey?: string) =>
    `/api/sprites/ct-${contactId}/png${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ""}`,
};

// ─── Office simulation (Visualize) ───────────────────────────────────────

export interface OfficeSnapshot {
  ts: number;
  entities: Array<import("./officeSim").SimEntity>;
  /** persona → ms epoch of a spawn failure within the last few minutes */
  fails: Record<string, number>;
  /** persona → unread bus messages */
  unread: Record<string, number>;
  /** persona → ms epoch of a spawn completed within the last minute */
  dones: Record<string, number>;
  /** most completed spawns today (WITA) — employee of the day */
  star: { name: string; count: number } | null;
  weather: "clear" | "rain";
  cat: import("./officeSim").CatState;
  /** external contacts standing at desks for the spawns they triggered */
  visitors: Array<import("./officeSim").VisitorState>;
  /** people from active observed chats, hanging out in the garden */
  guests: Array<import("./officeSim").GuestState>;
  observed: Array<import("./officeSim").ObservedSpec>;
  /** non-terminal assist sessions — papers on the operator desk */
  approvals: Array<{
    sid: string;
    token: string;
    state: string;
    observer: string;
    broker: string;
    subject_chat: string;
    title: string | null;
    created_at: string;
  }>;
}

export interface OfficeConfig {
  wander_min: number;
  rest_hours: number;
  interact_s: number;
  defaults: { wander_min: number; rest_hours: number; interact_s: number };
}

export const OfficeApi = {
  snapshot: () => req<OfficeSnapshot>("/office"),
  config: () => req<OfficeConfig>("/office/config"),
  setConfig: (patch: Partial<Pick<OfficeConfig, "wander_min" | "rest_hours" | "interact_s">>) =>
    req<OfficeConfig>("/office/config", { method: "POST", body: JSON.stringify(patch) }),
};

// ─── Dashboard ───────────────────────────────────────────────────────────

export const DashboardApi = {
  missionControl: () => req<MissionControl>("/dashboard/mission-control"),
};

export const AnalyticsApi = {
  overview: (window: import("./types").AnalyticsWindow) =>
    req<import("./types").AnalyticsOverview>(`/analytics/overview?window=${window}`),
};

// ─── Fleet wiki ──────────────────────────────────────────────────────────

export const WikiApi = {
  overview: () => req<import("./types").WikiOverview>("/wiki/overview"),
  queue: (status?: string) => req<{ items: import("./types").WikiQueueItem[] }>(`/wiki/queue${status ? `?status=${status}` : ""}`),
  submit: (body: { kind: string; payload: string; note?: string; topics?: string[] }) =>
    req<{ ok?: boolean; id?: number; error?: string }>("/wiki/submit", { method: "POST", body: JSON.stringify(body) }),
  retry: (id: number) => req<{ ok: boolean }>(`/wiki/items/${id}/retry`, { method: "POST" }),
  cancel: (id: number) => req<{ ok: boolean }>(`/wiki/items/${id}`, { method: "DELETE" }),
  search: (q: string, topics?: string) =>
    req<{ hits: import("./types").WikiHit[] }>(`/wiki/search?q=${encodeURIComponent(q)}${topics ? `&topics=${encodeURIComponent(topics)}` : ""}`),
  topic: (topic: string) => req<{ topic: string; entries: import("./types").WikiHit[] }>(`/wiki/topic/${encodeURIComponent(topic)}`),
  gaps: () => req<{ gaps: import("./types").WikiGap[] }>("/wiki/gaps"),
  lint: () => req<import("./types").WikiLint>("/wiki/lint"),
  deleteEntry: (id: number) => req<{ ok: boolean }>(`/wiki/entry/${id}`, { method: "DELETE" }),
  upload: (form: FormData) => req<{ ok?: boolean; id?: number; error?: string }>("/wiki/upload", { method: "POST", body: form }),
};
